import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { subscribeDebtors } from '../services/cari'
import type { DebtorBalance, DebtorEntry } from '../types'

interface CariContextValue {
  debtors: DebtorBalance[]
  entries: DebtorEntry[]
  /** Entries for one person, most recent first. */
  entriesFor: (debtorId: string) => DebtorEntry[]
  /** What is owed across every account. */
  totalOwedKurus: number
  loading: boolean
}

const CariContext = createContext<CariContextValue | null>(null)

/**
 * Accounts for people who pay later, live for as long as the feature is on.
 *
 * Subscribes only where the feature is switched on and the person can take
 * money, so an institution that has not been given the feature never opens
 * the channel or reads the tables, the flag is not just a hidden button,
 * and neither does a waiter's phone, which the policies refuse anyway.
 */
export function CariProvider({ children }: { children: ReactNode }) {
  const { tenantId, isAuthenticated, canSettle, hasFeature } = useAuth()
  // canSettle as well as the flag: a waiter cannot read these tables at all
  // (see supabase/cari.sql), so subscribing on their device would open a
  // channel and a pair of queries that can only ever come back empty.
  const enabled = isAuthenticated && canSettle && hasFeature('cari')

  const [debtors, setDebtors] = useState<DebtorBalance[]>([])
  const [entries, setEntries] = useState<DebtorEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tenantId || !enabled) {
      // Cleared rather than left as they were: switching institutions, or
      // having the feature turned off mid-session, must not leave another
      // kurum's names on screen.
      setDebtors([])
      setEntries([])
      setLoading(false)
      return
    }

    setLoading(true)
    const stop = subscribeDebtors(tenantId, (list, moves) => {
      setDebtors(list)
      setEntries(moves)
      setLoading(false)
    })
    return stop
  }, [tenantId, enabled])

  const value = useMemo<CariContextValue>(() => {
    const byDebtor = new Map<string, DebtorEntry[]>()
    for (const e of entries) {
      const list = byDebtor.get(e.debtorId)
      if (list) list.push(e)
      else byDebtor.set(e.debtorId, [e])
    }
    for (const list of byDebtor.values()) {
      list.sort((a, b) => b.createdAtLocal - a.createdAtLocal)
    }

    return {
      debtors,
      entries,
      entriesFor: (id) => byDebtor.get(id) ?? [],
      // Only what is owed. A person in credit does not reduce what the rest
      // of the institution is waiting to collect.
      totalOwedKurus: debtors.reduce(
        (sum, d) => sum + Math.max(0, d.balanceKurus),
        0,
      ),
      loading,
    }
  }, [debtors, entries, loading])

  return <CariContext.Provider value={value}>{children}</CariContext.Provider>
}

export function useCari(): CariContextValue {
  const ctx = useContext(CariContext)
  if (!ctx) throw new Error('useCari, CariProvider içinde kullanılmalı')
  return ctx
}
